import React, { useId } from 'react';

import { CHEVRON_DOWN, CHEVRON_RIGHT } from '../icons';
import FieldMessage from './FieldMessage';

/**
 * One step of the chart builder's question (plan §6.2): a white form panel
 * whose kicker names the step ("1 · Items"), the controls, any one-line
 * notices about a choice a later change cleared, and any message the server
 * or the draft has for the step as a whole.
 *
 * On a phone the steps FOLD (plan §6.7, the brief's "folds into steps"): the
 * kicker is the disclosure — the item form's Notes grammar — and a folded
 * step says its answer in one line under it ("Total of Amount"), so the
 * whole question reads top to bottom without opening anything. On a desktop
 * every step is open and the kicker is plain text.
 *
 *   step       the step's key ('items', 'measures', …), for tests and anchors
 *   number     its place in the question
 *   title      its kicker
 *   summary    the folded step's one line (phone)
 *   foldable   true on a phone
 *   open       whether a foldable step is open
 *   onToggle   () => void
 *   notices    [{ text }] cleared choices, one line each
 *   messages   [{ tone, text }] for the step as a whole (a refusal no control shows)
 */
export default function BuilderStep({
	step,
	number,
	title,
	summary,
	foldable = false,
	open = true,
	onToggle,
	notices = [],
	messages = [],
	children,
}) {
	const bodyId = `chart-step-${step}-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
	const kicker = <span className='micro-label form-panel__kicker'>{`${number} · ${title}`}</span>;
	const shown = !foldable || open;
	return (
		<section className={`form-panel chart-builder__step${foldable ? ' chart-builder__step--foldable' : ''}`} data-step={step} aria-label={title}>
			{foldable ? (
				<button
					type='button'
					className='form-panel__kicker-toggle chart-builder__step-toggle'
					aria-expanded={open ? 'true' : 'false'}
					aria-controls={bodyId}
					onClick={onToggle}
				>
					<span className='form-panel__kicker-chevron' aria-hidden='true'>{open ? <CHEVRON_DOWN /> : <CHEVRON_RIGHT />}</span>
					{kicker}
				</button>
			) : kicker}
			{foldable && !open && summary ? <div className='chart-builder__step-summary'>{summary}</div> : null}
			{foldable && !open && notices.length ? (
				// A choice a later change cleared is said on the fold, where it is seen (the A5 review's L-9).
				<ul className='chart-builder__notices chart-builder__notices--folded' aria-live='polite'>
					{notices.map((notice, i) => <li key={i}>{notice.text}</li>)}
				</ul>
			) : null}
			{foldable && !open && messages.some((m) => m.tone === 'error') ? (
				// A refusal on a folded step is said on the fold too, so it is never hidden.
				<FieldMessage messages={messages.filter((m) => m.tone === 'error').slice(0, 1)} />
			) : null}
			<div id={bodyId} className='chart-builder__step-body' hidden={!shown}>
				{notices.length && shown ? (
					<ul className='chart-builder__notices' aria-live='polite'>
						{notices.map((notice, i) => <li key={i}>{notice.text}</li>)}
					</ul>
				) : null}
				{children}
				<FieldMessage messages={messages} />
			</div>
		</section>
	);
}
